import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, User, Phone, PawPrint, Lock, Minus, Plus } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { quickAdd } from '../services/api';
import { toast } from 'sonner';

const QuickAdd = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    customer_name: '',
    customer_phone: location.state?.searchQuery || '',
    customer_password: '',
    pet_name: '',
    pet_species: 'dog',
    pet_breed: '',
    pet_gender: '',
    pet_color: '',
    pet_age: 0,
    pet_notes: '',
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validatePhone(formData.customer_phone)) {
      toast.error('Telefon numarası 0 ile başlamalı ve tam 11 haneli olmalıdır');
      return;
    }
    setLoading(true);
    try {
      const payload = { ...formData, pet_age: formData.pet_age || null };
      const response = await quickAdd(payload);
      toast.success(`Müşteri ve hasta oluşturuldu`);
      navigate(`/customer/${response.data.customer_id}`, {
        state: { newPetId: response.data.pet_id }
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || `Bir hata oluştu`);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    if (field === 'customer_phone') {
      value = value.replace(/\D/g, '').slice(0, 11);
    }
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const validatePhone = (phone) => {
    return /^0\d{10}$/.test(phone);
  };

  return (
    <div className="min-h-screen bg-[#F2F2F7]">
      <header className="bg-white px-4 pt-12 pb-4 shadow-sm sticky top-0 z-10">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="p-2 -ml-2 rounded-full hover:bg-gray-100" data-testid="back-btn">
            <ArrowLeft size={24} className="text-gray-900" />
          </button>
          <h1 className="text-xl font-bold text-gray-900 font-['Plus_Jakarta_Sans']">{`Hızlı Ekle`}</h1>
        </div>
      </header>

      <form onSubmit={handleSubmit} className="p-4 space-y-4">
        {/* Customer Section */}
        <div className="ios-card space-y-4">
          <h2 className="text-sm font-semibold text-[#8E8E93] uppercase tracking-wider">{`Müşteri Bilgileri`}</h2>
          
          <div className="space-y-2">
            <Label className="text-sm font-medium text-gray-700">{`Müşteri Adı *`}</Label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8E8E93]" size={20} />
              <Input type="text" placeholder="Ad Soyad" value={formData.customer_name} onChange={(e) => handleChange('customer_name', e.target.value)} className="pl-10 h-12 text-base bg-[#F2F2F7] border-0 rounded-xl" required data-testid="customer-name-input" />
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-sm font-medium text-gray-700">Telefon *</Label>
            <div className="relative">
              <Phone className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8E8E93]" size={20} />
              <Input type="tel" inputMode="numeric" maxLength={11} placeholder="05XXXXXXXXX" value={formData.customer_phone} onChange={(e) => handleChange('customer_phone', e.target.value)} className="pl-10 h-12 text-base bg-[#F2F2F7] border-0 rounded-xl" required data-testid="customer-phone-input" />
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-sm font-medium text-gray-700">{`Müşteri Şifresi (Opsiyonel)`}</Label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8E8E93]" size={20} />
              <Input type="password" placeholder={`Müşteri portali için şifre`} value={formData.customer_password} onChange={(e) => handleChange('customer_password', e.target.value)} className="pl-10 h-12 text-base bg-[#F2F2F7] border-0 rounded-xl" data-testid="customer-password-input" />
            </div>
          </div>
        </div>

        {/* Pet Section */}
        <div className="ios-card space-y-4">
          <h2 className="text-sm font-semibold text-[#8E8E93] uppercase tracking-wider">Hasta Bilgileri</h2>
          
          <div className="space-y-2">
            <Label className="text-sm font-medium text-gray-700">{`Hasta Adı *`}</Label>
            <div className="relative">
              <PawPrint className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8E8E93]" size={20} />
              <Input type="text" placeholder={`Hasta adı`} value={formData.pet_name} onChange={(e) => handleChange('pet_name', e.target.value)} className="pl-10 h-12 text-base bg-[#F2F2F7] border-0 rounded-xl" required data-testid="pet-name-input" />
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-sm font-medium text-gray-700">{`Tür *`}</Label>
            <Select value={formData.pet_species} onValueChange={(v) => handleChange('pet_species', v)}>
              <SelectTrigger className="h-12 text-base bg-[#F2F2F7] border-0 rounded-xl" data-testid="pet-species-select">
                <SelectValue placeholder={`Tür seçin`} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="dog">{`Köpek`}</SelectItem>
                <SelectItem value="cat">Kedi</SelectItem>
                <SelectItem value="bird">{`Kuş`}</SelectItem>
                <SelectItem value="other">{`Diğer`}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label className="text-sm font-medium text-gray-700">{`Irk`}</Label>
            <Input type="text" placeholder={`Örn: Golden Retriever`} value={formData.pet_breed} onChange={(e) => handleChange('pet_breed', e.target.value)} className="h-12 text-base bg-[#F2F2F7] border-0 rounded-xl" data-testid="pet-breed-input" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label className="text-sm font-medium text-gray-700">Cinsiyet</Label>
              <Select value={formData.pet_gender} onValueChange={(v) => handleChange('pet_gender', v)}>
                <SelectTrigger className="h-12 text-base bg-[#F2F2F7] border-0 rounded-xl" data-testid="pet-gender-select">
                  <SelectValue placeholder={`Seçin`} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="male">Erkek</SelectItem>
                  <SelectItem value="female">{`Dişi`}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-medium text-gray-700">Renk</Label>
              <Input type="text" placeholder={`Örn: Sarı`} value={formData.pet_color} onChange={(e) => handleChange('pet_color', e.target.value)} className="h-12 text-base bg-[#F2F2F7] border-0 rounded-xl" data-testid="pet-color-input" />
            </div>
          </div>

          {/* Age Stepper */}
          <div className="space-y-2">
            <Label className="text-sm font-medium text-gray-700">{`Yaş`}</Label>
            <div className="flex items-center gap-4">
              <button
                type="button"
                onClick={() => handleChange('pet_age', Math.max(0, formData.pet_age - 1))}
                className="w-12 h-12 rounded-xl bg-[#F2F2F7] flex items-center justify-center text-gray-700 hover:bg-gray-200 transition-colors"
                data-testid="pet-age-minus"
              >
                <Minus size={20} />
              </button>
              <div className="flex-1 text-center">
                <span className="text-2xl font-bold text-gray-900" data-testid="pet-age-value">
                  {formData.pet_age || 0}
                </span>
                <p className="text-xs text-[#8E8E93]">{`yaşında`}</p>
              </div>
              <button
                type="button"
                onClick={() => handleChange('pet_age', Math.min(25, formData.pet_age + 1))}
                className="w-12 h-12 rounded-xl bg-[#F2F2F7] flex items-center justify-center text-gray-700 hover:bg-gray-200 transition-colors"
                data-testid="pet-age-plus"
              >
                <Plus size={20} />
              </button>
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-sm font-medium text-gray-700">Notlar</Label>
            <Textarea placeholder={`Özel notlar...`} value={formData.pet_notes} onChange={(e) => handleChange('pet_notes', e.target.value)} className="text-base bg-[#F2F2F7] border-0 rounded-xl min-h-[80px]" data-testid="pet-notes-input" />
          </div>
        </div>

        <Button type="submit" disabled={loading} className="w-full h-14 text-base font-semibold bg-[#6b6de3] hover:bg-[#5658c4] rounded-full" data-testid="quick-add-submit-btn">
          {loading ? 'Kaydediliyor...' : `Kaydet ve Devam Et`}
        </Button>
      </form>
    </div>
  );
};

export default QuickAdd;
